import { useState, useMemo, useEffect } from "react";
import Progress from "./progress";
import styles from "./onboarding.module.css";
import DynamicForm from "../DynamicForm";
import { Fields } from "./onboardingUtils";
import { ToastContainer } from "react-toastify";

import useAuth from "../../hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { notifyError, notifySuccess } from "../../utils";
import { useDispatch, useSelector } from "react-redux";
import { upsertProfile } from "../../redux/slices/artistProfileSlice";
import { fetchCategories } from "../../redux/slices/artistSlice";

export const labelToKeyMap = {
  Singer: "singer",
  Dancer: "dancer",
  Magician: "magician",
  Instrumentalist: "instrumentalist",
  Musician: "musician",
  DJ: "dj",
  "Dance Group": "dance_group",
  Rapper: "rapper",
  "Beat Boxer": "beat_boxer",
  "Video Editor": "video_editor",
  Photographer: "photography",
};
const genreMapping = {
  Singer: "singerGenre",
  Dancer: "dancerGenre",
  Magician: "magicianGenre",
  Instrumentalist: "instrumentalistGenre",
  Musician: "musicianGenre",
  DJ: "djGenre",
  Rapper: "rapperGenre",
  "Beat Boxer": "beatboxGenre",
  "Video Editor": "vedioeditorGenre",
  Photographer: "photographyGenre",
  "Dance Group": "dancegroupGenre",
};
export const genreArray = Object.values(genreMapping);

const StepComponent = ({
  step,
  setOnboardingData,
  handleNextStep,
  onboardingData,
  setCurrentStep,
  profilePictureLoading,
  setProfilePictureLoading,
  handleUpsertProfile,
}) => {
  const genreMapping = {
    Singer: "singerGenre",
    Dancer: "dancerGenre",
    Magician: "magicianGenre",
    Instrumentalist: "instrumentalistGenre",
    Musician: "musicianGenre",
    DJ: "djGenre",
    Rapper: "rapperGenre",
    "Beat Boxer": "beatboxGenre",
    "Video Editor": "vedioeditorGenre",
    Photographer: "photographyGenre",
    "Dance Group": "dancegroupGenre",
  };

  const getFilteredFields = (step, onboardingData) => {
    let filteredFields;

    if (step === 1) {
      const selectedArtistTypes = onboardingData.name || [];

      filteredFields = Fields[1].filter((field) =>
        Object.entries(genreMapping).some(
          ([artistType, genreField]) =>
            field.name === genreField &&
            selectedArtistTypes.includes(artistType)
        )
      );
    } else {
      filteredFields = Fields[step];
    }

    // 🔥 Remove empty fields array (if length is 0)
    return filteredFields.length > 0 ? filteredFields : null;
  };
  return (
    <>
      {getFilteredFields(step, onboardingData) && (
        <DynamicForm
          fields={getFilteredFields(step, onboardingData)}
          setOnboardingData={setOnboardingData}
          handleNextStep={handleNextStep}
          onboardingData={onboardingData}
          setCurrentStep={setCurrentStep}
          profilePictureLoading={profilePictureLoading}
          setProfilePictureLoading={setProfilePictureLoading}
          handleUpsertProfile={handleUpsertProfile}
        />
      )}
    </>
  );
};

export default function Onboarding() {
  const [currentStep, setCurrentStep] = useState(0);
  const dispatch = useDispatch();
  const [onboardingData, setOnboardingData] = useState(0);
  const { userOnboarded, upsertError } = useSelector(
    (state) => state.artistProfile
  );
  const [profilePictureLoading, setProfilePictureLoading] = useState(false);
  const { categories } = useSelector((state) => state.artists);

  const progressPercentage = ((currentStep + 1) / 9) * 100;
  const { user } = useAuth();

  const navigate = useNavigate();

  useEffect(() => {
    dispatch(fetchCategories());
    if (user?.onboarded) {
      navigate("/");
    }
  }, []);

  // useEffect(() => {
  //   if (user?.onboarded) {
  //     navigate("/");
  //   }
  // }, [user]);

  // useEffect(() => {
  //   let timerId;
  //   if (userOnboarded) {
  //     notifySuccess("Yayy!! You are Onboarded!");
  //     timerId = setTimeout(() => {
  //       navigate("/dashboard/artist");
  //     }, 2000);
  //   }
  //   return () => {
  //     clearTimeout(timerId);
  //   };
  // }, [userOnboarded]);

  useEffect(() => {
    if (upsertError) {
      notifyError("Something went wrong");
    }
  }, [upsertError]);

  const handleUpsertProfile = async (restrictRedirection) => {
    try {
      let payloadToSend = {
        categories: [],
        ...onboardingData,
      };

      const getGenre = (ele) => {
        switch (ele) {
          case "Singer":
            return onboardingData.singerGenre;
          case "Dancer":
            return onboardingData.dancerGenre;
          case "Magician":
            return onboardingData.magicianGenre;
          case "Instrumentalist":
            return onboardingData.instrumentalistGenre;
          case "Musician":
            return onboardingData.musicianGenre;
          case "DJ":
            return onboardingData.djGenre;
          case "Rapper":
            return onboardingData.rapperGenre;
          case "Beat Boxer":
            return onboardingData.beatboxGenre;
          case "Video Editor":
            return onboardingData.vedioeditorGenre;
          case "Photographer":
            return onboardingData.photographyGenre;
          case "Dance Group":
            return onboardingData.dancegroupGenre;
          default:
            return null; // fallback in case of unknown category
        }
      };

      const genreMapping = {
        Singer: "singerGenre",
        Dancer: "dancerGenre",
        Magician: "magicianGenre",
        Instrumentalist: "instrumentalistGenre",
        Musician: "musicianGenre",
        DJ: "djGenre",
        Rapper: "rapperGenre",
        "Beat Boxer": "beatboxGenre",
        "Video Editor": "vedioeditorGenre",
        Photographer: "photographyGenre",
        "Dance Group": "dancegroupGenre",
      };

      for (let i = 0; i < onboardingData?.name?.length; i++) {
        const ele = onboardingData.name[i];
        const genreArray = getGenre(ele);
        const categoryObject = {
          name: labelToKeyMap[ele],
          genre: genreArray,
        };
        payloadToSend.categories.push(categoryObject);
      }
      delete payloadToSend["name"];
      Object.values(genreMapping)?.map((data) => {
        delete payloadToSend[data];
      });
      if (onboardingData["experience_years"]) {
        const rangeStr = String(onboardingData["experience_years"]);

        const [min, max] = rangeStr.split("-").map(Number);

        const rangeObj = { min, max };
        payloadToSend["experience_years"] = rangeObj;
        if (!rangeObj.min || !rangeObj.max) {
          payloadToSend["experience_years"] = {
            min: 0,
            max: 1,
          };
        }
      }

      if (payloadToSend?.photo) {
        delete payloadToSend["photo"];
      }

      dispatch(upsertProfile(payloadToSend));
      if (restrictRedirection) {
        console.error("restricting redirection");
      } else {
        notifySuccess("Yayy!! You are Onboarded!");
        navigate("/dashboard/artist");
      }

      const formData = new FormData();
      formData.append("profilePicture", onboardingData["profilePicture"]);
    } catch (error) {
      console.error(
        "Error updating profile:",
        error.response?.data || error.message
      );
    }
  };

  const nextStep = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    if (
      currentStep === 0 &&
      (!onboardingData?.name || onboardingData?.name?.length === 0)
    ) {
      notifyError("Artist type is mandatory");
      return;
    } else if (currentStep === 2 && !onboardingData?.experience_years) {
      notifyError("Experience is mandatory");
      return;
    } else if (
      currentStep === 3 &&
      (!onboardingData?.things_i_enjoy ||
        onboardingData?.things_i_enjoy?.length === 0)
    ) {
      notifyError("Artistic style is mandatory");
      return;
    } else if (
      currentStep === 5 &&
      (!onboardingData.crowd_size_preference ||
        onboardingData.crowd_size_preference?.length === 0)
    ) {
      notifyError("Crowd size is mandatory");
      return;
    } else if (
      currentStep === 6 &&
      (!onboardingData.available_locations ||
        onboardingData.available_locations?.length === 0)
    ) {
      notifyError("Available location is mandatory");
      return;
    } else if (currentStep == 8 && !onboardingData?.photo) {
      notifyError("Profile picture is mandatory");
      return;
    }
    if (currentStep < 8) {
      setCurrentStep(currentStep + 1);
    } else {
      handleUpsertProfile();
    }
  };

  const prevStep = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const MemoizedStepComponent = useMemo(
    () => (
      <StepComponent
        step={currentStep}
        setOnboardingData={setOnboardingData}
        handleNextStep={nextStep}
        onboardingData={onboardingData}
        profilePictureLoading={profilePictureLoading}
        setProfilePictureLoading={setProfilePictureLoading}
        handleUpsertProfile={handleUpsertProfile}
      />
    ),
    [currentStep]
  );

  return (
    <>
      <ToastContainer />
      <div className={styles.root}>
        <h1 className={styles.heading}>{`Hi ${user?.username} 👋 
       ,
       please complete Onboarding Process to continue...`}</h1>
        <div className={styles.onboardingRoot}>
          <div className={styles.onboardingContainer}>
            <div className={styles.divider}>
              <div className={styles.scroll}>
                <div>
                  <Progress
                    value={progressPercentage}
                    className={styles.progressBar}
                  />
                  <div className={styles.indicator}>{`${Math.floor(
                    progressPercentage
                  )}% Completed`}</div>
                  {MemoizedStepComponent}
                  {currentStep === 9 && (
                    <h2 className={styles.center}>
                      {"You are Onboarded, Show some fire on stage 🔥"}
                    </h2>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className={styles.buttonGroup}>
            {currentStep !== 0 && (
              <button
                onClick={prevStep}
                disabled={currentStep === 0}
                className={styles.backBtn}
              >
                {"←"}
              </button>
            )}
            <button
              disabled={currentStep === 8 && profilePictureLoading}
              onClick={nextStep}
              className={styles.btn}
            >
              {currentStep < 8
                ? "Continue"
                : currentStep === 8 && profilePictureLoading
                ? "Please wait, Uploading.."
                : "Submit"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
